// SubscriptionHistory.js
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, Divider, CircularProgress, Alert, Chip } from '@mui/material';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axiosInstance from '../../../utils/axiosInstance';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    } catch (e) {
        console.warn("Date/Time formatting failed:", e);
        return dateString;
    }
};

const getEventDisplay = (changeType) => {
    const eventTypes = {
        'initial': { label: 'Subscribed', icon: <SubscriptionsIcon />, color: '#3b82f6' },
        'trial_start': { label: 'Trial Started', icon: <PlayArrowIcon />, color: '#8b5cf6' },
        'trial_convert': { label: 'Trial Converted', icon: <AutorenewIcon />, color: '#059669' },
        'renewal': { label: 'Renewed', icon: <AutorenewIcon />, color: '#059669' },
        'immediate_upgrade': { label: 'Upgraded', icon: <UpgradeIcon />, color: '#059669' },
        'immediate_downgrade': { label: 'Downgraded', icon: <TrendingDownIcon />, color: '#f59e0b' },
        'cancel_scheduled': { label: 'Cancellation Scheduled', icon: <CancelIcon />, color: '#ef4444' },
        'cancel_immediate': { label: 'Cancelled', icon: <CancelIcon />, color: '#ef4444' },
        'reactivate': { label: 'Reactivated', icon: <RestoreIcon />, color: '#059669' },
        'resubscribe_active': { label: 'Resubscribed', icon: <SubscriptionsIcon />, color: '#3b82f6' },
        'resubscribe_inactive': { label: 'Resubscribed', icon: <SubscriptionsIcon />, color: '#3b82f6' },
        'system_downgrade': { label: 'System Downgrade', icon: <TrendingDownIcon />, color: '#ef4444' },
        'trial_plan_change': { label: 'Trial Plan Changed', icon: <SwapHorizIcon />, color: '#8b5cf6' },
        'account_conversion': { label: 'Account Converted', icon: <SwapHorizIcon />, color: '#8b5cf6' },
    };
    return eventTypes[changeType] || { label: changeType || 'Unknown', icon: <SubscriptionsIcon />, color: '#94a3b8' };
};

const SubscriptionHistory = () => {
    const [history, setHistory] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axiosInstance.get('/api/subscriptions/subscriptions/history');
                setHistory(response.data || []);
            } catch (err) {
                console.error("Error fetching subscription history:", err);
                setError(err.response?.data?.message || err.message || "Failed to load subscription history.");
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                Subscription History
            </Typography>
            {history.length === 0 ? (
                <Typography sx={{ color: '#64748b' }}>No subscription history found.</Typography>
            ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    {history.map((event, index) => {
                        const eventDisplay = getEventDisplay(event.change_type);
                        return (
                            <React.Fragment key={event.id}>
                                <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                                    <ListItemIcon sx={{ minWidth: 44, mt: 0.5 }}>
                                        <Box sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '10px',
                                            bgcolor: `${eventDisplay.color}15`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: eventDisplay.color,
                                        }}>
                                            {eventDisplay.icon}
                                        </Box>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                    {eventDisplay.label}
                                                </Typography>
                                                {event.new_plan_name && (
                                                    <Chip
                                                        label={event.new_plan_name}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: `${eventDisplay.color}15`,
                                                            color: eventDisplay.color,
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                )}
                                                {event.amount_charged && event.amount_charged > 0 && (
                                                    <Chip
                                                        label={`$${event.amount_charged.toFixed(2)}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(5,150,105,0.1)',
                                                            color: '#059669',
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <React.Fragment>
                                                <Typography
                                                    sx={{ display: 'block', color: '#64748b', fontSize: '0.85rem' }}
                                                    component="span"
                                                    variant="body2"
                                                >
                                                    {formatDate(event.change_timestamp)}
                                                </Typography>
                                                {event.previous_plan_name && event.new_plan_name && event.previous_plan_name !== event.new_plan_name && (
                                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.5 }}>
                                                        {event.previous_plan_name} → {event.new_plan_name}
                                                    </Typography>
                                                )}
                                                {event.notes && (
                                                    <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>
                                                        {event.notes}
                                                    </Typography>
                                                )}
                                            </React.Fragment>
                                        }
                                    />
                                </ListItem>
                                {index < history.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

export default SubscriptionHistory;
